import { Component, inject, Input, OnInit, signal } from "@angular/core";

import { FormsModule } from "@angular/forms";
import { OperationRequirement, OperationRequirementService } from "../../../_services/operation-requirement.service";
import { ArticleService } from "../../../_services";
import { ArticleTemplate } from "../../../_models";
import { StagingConfigService } from "../../../_services/staging-config.service";
import { firstValueFrom } from "rxjs";

@Component({
  selector: "app-operation-requirements",
  standalone: true,
  imports: [FormsModule],
  template: `
    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <h4 class="font-semibold text-sm">Artikelbedarf</h4>
        <button class="bm-btn bm-btn-ghost bm-btn-xs" (click)="showAddForm.set(true)" [disabled]="showAddForm()">
          + Hinzufügen
        </button>
      </div>

      <!-- Bestehende Requirements -->
      @for (req of requirements(); track req.id) {
        <div class="flex items-center gap-2 bg-base-200 rounded px-3 py-1.5 text-sm">
          <span
            class="bm-badge bm-badge-xs"
            [class.bm-badge-info]="req.action_type === 'STAGING'"
            [class.bm-badge-warning]="req.action_type === 'VERSAND'"
          >
            {{ req.action_type }}
          </span>
          <span class="font-medium">{{ req.quantity }}x</span>
          <span class="flex-1">{{ req.article_template_description || req.free_text }}</span>
          <button class="bm-btn bm-btn-ghost bm-btn-xs bm-btn-square" (click)="deleteRequirement(req)">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-3.5 h-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      }

      @if (requirements().length === 0 && !showAddForm()) {
        <span class="text-xs text-base-content/50">Kein Artikelbedarf definiert</span>
      }

      <!-- Hinzufügen-Formular -->
      @if (showAddForm()) {
        <div class="border border-base-300 rounded p-3 flex flex-col gap-2">
          <div class="flex gap-2">
            <select
              class="bm-select bm-select-sm w-28"
              [(ngModel)]="newActionType"
              (ngModelChange)="onActionTypeChange()"
            >
              <option value="STAGING">Staging</option>
              <option value="VERSAND">Versand</option>
            </select>
            <input
              type="number"
              class="bm-input bm-input-sm w-16"
              [(ngModel)]="newQuantity"
              min="1"
              placeholder="Anz."
            />
          </div>

          <!-- Artikelvorlagen-Suche -->
          <div class="relative">
            @if (newActionType === "STAGING") {
              <!-- Staging: Nur Projekt-Artikelvorlagen per Select -->
              <select
                class="bm-select bm-select-sm w-full"
                [(ngModel)]="selectedStagingTemplateId"
                (ngModelChange)="onStagingTemplateSelect($event)"
              >
                <option [ngValue]="null">Artikelvorlage wählen...</option>
                @for (tpl of stagingTemplates(); track tpl.id) {
                  <option [ngValue]="tpl.id">{{ tpl.description }}</option>
                }
              </select>
            } @else {
              <!-- Versand: Artikelvorlagen-Suche mit Freitext-Fallback -->
              <input
                type="text"
                class="bm-input bm-input-sm w-full"
                [(ngModel)]="newSearchText"
                (input)="onSearchInput()"
                placeholder="Artikelvorlage suchen oder Freitext..."
              />
              @if (searchResults().length > 0) {
                <div
                  class="absolute z-10 w-full mt-1 border border-base-300 rounded bg-base-100 shadow-lg max-h-32 overflow-y-auto"
                >
                  @for (result of searchResults(); track result.id) {
                    <div
                      class="px-3 py-1.5 text-sm cursor-pointer hover:bg-base-200"
                      (mousedown)="selectTemplate(result)"
                    >
                      {{ result.description }}
                      @if (result.part_nr) {
                        <span class="text-base-content/50">({{ result.part_nr }})</span>
                      }
                    </div>
                  }
                </div>
              }
            }
          </div>

          <div class="flex gap-2 justify-end">
            <button class="bm-btn bm-btn-ghost bm-btn-xs" (click)="cancelAdd()">Abbrechen</button>
            <button class="bm-btn bm-btn-primary bm-btn-xs" [disabled]="!canSave()" (click)="saveRequirement()">
              Speichern
            </button>
          </div>
        </div>
      }
    </div>
  `,
})
export class OperationRequirementsComponent implements OnInit {
  @Input() operationId!: number;
  @Input() projectId?: number;

  private service = inject(OperationRequirementService);
  private articleService = inject(ArticleService);
  private stagingConfigService = inject(StagingConfigService);

  protected requirements = signal<OperationRequirement[]>([]);
  protected showAddForm = signal<boolean>(false);
  protected searchResults = signal<ArticleTemplate[]>([]);
  protected selectedTemplate = signal<ArticleTemplate | null>(null);

  // Staging-Artikelvorlagen (nur die aus Staging-Configs des Projekts)
  protected stagingTemplates = signal<{ id: number; description: string }[]>([]);
  protected allTemplates = signal<ArticleTemplate[]>([]);

  protected newActionType: "STAGING" | "VERSAND" = "STAGING";
  protected newQuantity = 1;
  protected newSearchText = "";
  protected selectedStagingTemplateId: number | null = null;

  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  async ngOnInit() {
    this.requirements.set(await this.service.loadRequirements(this.operationId));
    await this.loadTemplates();
  }

  private async loadTemplates() {
    // Alle Artikelvorlagen laden (für Versand-Suche)
    const all = await firstValueFrom(this.articleService.loadArticleTemplates());
    this.allTemplates.set(all || []);

    // Staging-Artikelvorlagen aus den Staging-Configs des Projekts laden
    if (this.projectId) {
      await this.stagingConfigService.loadConfigs(this.projectId);
      const configs = this.stagingConfigService.configs();
      const templates = configs
        .filter((c) => c.enabled && c.article_template_id && c.article_template_description)
        .map((c) => ({ id: c.article_template_id!, description: c.article_template_description! }));
      // Duplikate entfernen
      const unique = [...new Map(templates.map((t) => [t.id, t])).values()];
      this.stagingTemplates.set(unique);
    }
  }

  onActionTypeChange() {
    // Bei Typ-Wechsel Auswahl zurücksetzen
    this.selectedTemplate.set(null);
    this.selectedStagingTemplateId = null;
    this.newSearchText = "";
    this.searchResults.set([]);
  }

  // --- Staging: Select-Auswahl ---

  onStagingTemplateSelect(templateId: number | null) {
    if (!templateId) {
      this.selectedTemplate.set(null);
      return;
    }
    const tpl = this.stagingTemplates().find((t) => t.id === templateId);
    if (tpl) {
      // Als ArticleTemplate-kompatibles Objekt setzen
      this.selectedTemplate.set({ id: tpl.id, description: tpl.description } as ArticleTemplate);
    }
  }

  // --- Versand: Artikelvorlagen-Suche ---

  onSearchInput() {
    this.selectedTemplate.set(null);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);

    if (this.newSearchText.trim().length < 2) {
      this.searchResults.set([]);
      return;
    }

    this.searchDebounce = setTimeout(() => this.performSearch(), 300);
  }

  private performSearch() {
    const query = this.newSearchText.toLowerCase();
    const results = this.allTemplates().filter(
      (t) => t.description?.toLowerCase().includes(query) || t.part_nr?.toLowerCase().includes(query),
    );
    this.searchResults.set(results.slice(0, 15));
  }

  selectTemplate(template: ArticleTemplate) {
    this.selectedTemplate.set(template);
    this.newSearchText = template.description;
    this.searchResults.set([]);
  }

  canSave(): boolean {
    if (this.newQuantity <= 0) return false;
    if (this.newActionType === "STAGING") {
      // Staging: Nur mit ausgewählter Artikelvorlage
      return this.selectedTemplate() !== null;
    }
    // Versand: Artikelvorlage oder Freitext
    return this.selectedTemplate() !== null || this.newSearchText.trim().length > 0;
  }

  async saveRequirement() {
    const template = this.selectedTemplate();
    const data: Partial<OperationRequirement> = {
      action_type: this.newActionType,
      quantity: this.newQuantity,
    };

    if (template) {
      data.article_template_id = template.id;
    } else {
      data.free_text = this.newSearchText.trim();
    }

    const result = await this.service.createRequirement(this.operationId, data);
    if (result) {
      this.requirements.update((reqs) => [...reqs, result]);
      this.cancelAdd();
    }
  }

  async deleteRequirement(req: OperationRequirement) {
    const success = await this.service.deleteRequirement(req.id);
    if (success) {
      this.requirements.update((reqs) => reqs.filter((r) => r.id !== req.id));
    }
  }

  cancelAdd() {
    this.showAddForm.set(false);
    this.newActionType = "STAGING";
    this.newQuantity = 1;
    this.newSearchText = "";
    this.selectedTemplate.set(null);
    this.selectedStagingTemplateId = null;
    this.searchResults.set([]);
  }
}
